// Account status type
export type AccountStatus = 'Pending' | 'Active' | 'Inactive';

// Service use type for location-level accounts
export type ServiceUseType = 'Clinical' | 'InjuryMgmt';

export const ServiceUseTypeLabels: Record<ServiceUseType, string> = {
  Clinical: 'Clinical Services',
  InjuryMgmt: 'Injury Management',
};

export const ServiceUseTypeColors: Record<ServiceUseType, string> = {
  Clinical: '#0d6efd',
  InjuryMgmt: '#fd7e14',
};

// Status labels and colors for UI display
export const AccountStatusLabels: Record<AccountStatus, string> = {
  Pending: 'Pending',
  Active: 'Active',
  Inactive: 'Inactive',
};

export const AccountStatusColors: Record<AccountStatus, string> = {
  Pending: '#f0ad4e',
  Active: '#28a745',
  Inactive: '#6c757d',
};

// Org-level account
export interface ClinicClientAccount {
  id: number;
  clinicId: number;
  clinicName?: string;
  clinicCode?: string;
  customerId: number;
  customerName?: string;
  /** Navision identifier (SLV2-2823). */
  customerXmId?: string | null;
  accountNumber?: string;
  accountStatus: AccountStatus;
  notes?: string;
  createdDate: string;
  modifiedDate?: string;
}

export interface CreateClinicClientAccountRequest {
  clinicId: number;
  customerId: number;
  accountNumber?: string;
  accountStatus?: string;
  notes?: string;
}

export interface UpdateClinicClientAccountRequest {
  accountNumber?: string;
  accountStatus?: string;
  notes?: string;
}

// Location-level account
export interface ClinicLocationClientAccount {
  id: number;
  clinicId: number;
  clinicName?: string;
  clinicCode?: string;
  clinicAddress?: string;
  clinicCity?: string;
  clinicState?: string;
  clinicZip?: string;
  clinicPhone?: string;
  customerLocationId: number;
  customerLocationName?: string;
  customerId: number;
  customerName?: string;
  /** Navision identifier (SLV2-2823). */
  customerXmId?: string | null;
  accountNumber?: string;
  accountStatus: AccountStatus;
  serviceTypeKey: ServiceUseType;
  isPreferred: boolean;
  isPrimary: boolean;
  /** Workers'-comp panel flag (SLV2-2919) — only ever true on InjuryMgmt records. */
  isPanelState: boolean;
  notes?: string;
  createdDate: string;
  modifiedDate?: string;
}

export interface CreateClinicLocationClientAccountRequest {
  clinicId: number;
  customerLocationId: number;
  accountNumber?: string;
  accountStatus?: string;
  serviceTypeKey: ServiceUseType;
  isPreferred?: boolean;
  isPrimary?: boolean;
  isPanelState?: boolean;
  notes?: string;
}

export interface UpdateClinicLocationClientAccountRequest {
  accountNumber?: string;
  accountStatus?: string;
  isPreferred?: boolean;
  isPrimary?: boolean;
  /** InjuryMgmt records only — the DB check constraint rejects it on Clinical rows. */
  isPanelState?: boolean;
  notes?: string;
}

/** Per-service-type flags for the dual-create wizard (SLV2-2919). */
export interface ServiceTypeFlags {
  isPreferred: boolean;
  isPrimary: boolean;
}

export interface InjuryMgmtFlags extends ServiceTypeFlags {
  isPanelState: boolean;
}

/**
 * Creates the Clinical Services and/or Injury Management relationship rows for
 * a (location, clinic) pair in one call — both rows in one DB transaction.
 * Omit a section to skip that service type; at least one is required.
 */
export interface CreateClinicLocationClientAccountsBatchRequest {
  clinicId: number;
  customerLocationId: number;
  clinical?: ServiceTypeFlags;
  injuryMgmt?: InjuryMgmtFlags;
  accountNumber?: string;
  accountStatus?: string;
  notes?: string;
}

// Customer search
export interface Customer {
  id: number;
  name: string;
  accountNumber?: string;
  /** Navision identifier (SLV2-2823) — shown beside the name so duplicate company names are distinguishable. */
  xmId?: string | null;
}

export interface CustomerLocation {
  id: number;
  customerId: number;
  customerName?: string;
  /** Navision identifiers (SLV2-2823). */
  customerXmId?: string | null;
  xmId?: string | null;
  name: string;
  street?: string;
  suite?: string;
  city?: string;
  state?: string;
  zip?: string;
  latitude?: number;
  longitude?: number;
}

// Relationship notes
export interface RelationshipNote {
  id: number;
  referenceTable: string;
  referenceId: number;
  noteText: string;
  noteType?: string;
  createdBy?: number;
  createdAt: string;
  modifiedBy?: number;
  modifiedAt?: string;
  isActive: boolean;
}

export interface CreateRelationshipNoteRequest {
  noteText: string;
  noteType?: string;
}

// Hierarchy
export interface ClinicHierarchy {
  id: number;
  clinicId: number;
  clinicName?: string;
  clinicCode?: string;
  clinicCity?: string;
  clinicState?: string;
  parentClinicId: number;
  parentClinicName?: string;
  parentClinicCode?: string;
  isActive: boolean;
  children?: ClinicHierarchy[];
}

export interface CreateClinicHierarchyRequest {
  clinicId: number;
  parentClinicId: number;
}

export interface UnassignedClinic {
  id: number;
  name: string;
  code?: string;
  city?: string;
  state?: string;
}
